import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DoneIcon from '@mui/icons-material/Done';
import CancelIcon from '@mui/icons-material/Cancel';
import WorkIcon from '@mui/icons-material/Work';
import AddIcon from '@mui/icons-material/Add';
import Menu from '../components/Menu';
import api from '../utils/apiClient';
import useRefetchOnHostingChange from '../hooks/useRefetchOnHostingChange';
import { getLocale } from '../utils/dateLocale';
import NotificationContainer from '../components/NotificationContainer';
import { useNotifications } from '../context/NotificationContext';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../hooks/useAuth';
import { normalizeRole, isClient, isHoster, isAdminOrSuperAdmin } from '../utils/roles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getStatusColor = (status) => {
  switch (status) {
    case 'open': return 'warning';
    case 'in_progress': return 'info';
    case 'waiting-agent': return 'default';
    case 'done': return 'success';
    case 'rejected': return 'error';
    case 'cancelled': return 'default';
    default: return 'default';
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'open': return t('ticketsStatusOpen');
    case 'in_progress': return t('ticketsStatusInProgress');
    case 'waiting-agent': return t('ticketsStatusWaitingAgent');
    case 'done': return t('ticketsStatusDone');
    case 'rejected': return t('ticketsStatusRejected');
    case 'cancelled': return t('ticketsStatusCancelled');
    default: return status || '-';
  }
};

const getTypeLabel = (type, t) => {
  switch (type) {
    case 'manual': return t('ticketsTypeManual');
    case 'restart': return t('ticketsTypeRestart');
    case 'hardreset': return t('ticketsTypeHardreset');
    case 'change_pool': return t('ticketsTypeChangePool');
    case 'log': return t('ticketsTypeLog');
    default: return type || '-';
  }
};

const getPriorityColor = (p) => {
  switch (p) {
    case 'urgent': return 'error';
    case 'high': return 'warning';
    case 'normal': return 'primary';
    case 'low': return 'default';
    default: return 'default';
  }
};

const getPriorityLabel = (p, t) => {
  switch (p) {
    case 'urgent': return t('ticketsPriorityUrgent');
    case 'high': return t('ticketsPriorityHigh');
    case 'normal': return t('ticketsPriorityNormal');
    case 'low': return t('ticketsPriorityLow');
    default: return p || '-';
  }
};

const formatDate = (val) => {
  if (!val) return '-';
  try {
    return new Date(val).toLocaleString(getLocale());
  } catch (e) {
    return '-';
  }
};

const getKindLabel = (kind, t) => {
  switch (kind) {
    case 'client_to_hoster': return t('ticketsKindClientToHoster');
    case 'hoster_to_admin': return t('ticketsKindHosterToAdmin');
    default: return kind || '';
  }
};

const getKindColor = (kind) => {
  switch (kind) {
    case 'client_to_hoster': return 'primary';
    case 'hoster_to_admin': return 'warning';
    default: return 'default';
  }
};

const getSourceLabel = (source, t) => {
  switch (source) {
    case 'interface': return t('ticketsSourceInterface');
    case 'email': return t('ticketsSourceEmail');
    case 'telegram': return t('ticketsSourceTelegram');
    default: return source || '';
  }
};

export default function Tickets() {
  const theme = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation('admin');
  const { showError, showSuccess } = useNotifications();
  const role = normalizeRole(user?.role);
  const clientView = isClient(role);
  const hosterView = isHoster(role);
  const adminView = isAdminOrSuperAdmin(role);
  const isSuperAdmin = role === 'super_admin';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // admin без super_admin -- read-only по тикетам клиента (kind=client_to_hoster). hoster и super_admin -- write всегда.
  const canWriteTicket = (ticket) => {
    if (hosterView || isSuperAdmin) return true;
    if (adminView) return ticket.kind !== 'client_to_hoster';
    return false;
  };

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [kindTab, setKindTab] = useState(() => {
    if (isHoster(role)) return 'client_to_hoster';
    if (isAdminOrSuperAdmin(role)) return 'hoster_to_admin';
    return '';
  });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTicket, setDetailsTicket] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, action: '', ticket: null, comment: '' });
  const [processing, setProcessing] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    client: null,
    devices: [],
    title: '',
    description: '',
    priority: 'normal',
    source: 'interface'
  });
  const [creating, setCreating] = useState(false);
  const [clientsList, setClientsList] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientDevices, setClientDevices] = useState([]);
  const [clientDevicesLoading, setClientDevicesLoading] = useState(false);

  const [escalateDialog, setEscalateDialog] = useState({ open: false, ticket: null, comment: '', error: '' });
  const [escalating, setEscalating] = useState(false);

  // Гонка при быстром переключении клиента в диалоге создания: ответ предыдущего клиента может прийти после нового
  const clientDevicesReqRef = useRef(0);

  // Для admin вкладка "Все" не передаёт kind в API -- видит и client_to_hoster (read-only), и hoster_to_admin
  const apiKindFilter = (adminView && kindTab === 'all') ? '' : kindTab;

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const resp = await api.get(`${API_BASE_URL}tickets`, {
        params: {
          page: 1,
          limit: 50,
          status: statusFilter || undefined,
          kind: apiKindFilter || undefined
        }
      });
      setTickets(Array.isArray(resp?.data?.results) ? resp.data.results : []);
    } catch (e) {
      console.error('Ошибка загрузки тикетов:', e);
      setTickets([]);
      showError(t('ticketsErrorLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Если user догружается асинхронно после mount, kindTab остаётся '' и Tabs не находит соответствие
  useEffect(() => {
    if (kindTab) return;
    if (isHoster(role)) setKindTab('client_to_hoster');
    else if (isAdminOrSuperAdmin(role)) setKindTab('hoster_to_admin');
  }, [role, kindTab]);

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, kindTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useRefetchOnHostingChange(fetchTickets);

  const openTicketDetails = async (ticketId) => {
    if (!ticketId) return;
    try {
      setDetailsLoading(true);
      setDetailsOpen(true);
      const resp = await api.get(`${API_BASE_URL}tickets/${ticketId}`);
      setDetailsTicket(resp?.data?.ticket || null);
    } catch (e) {
      console.error('Ошибка загрузки деталей тикета:', e);
      setDetailsTicket(null);
      showError(t('ticketsErrorLoadDetails'));
    } finally {
      setDetailsLoading(false);
    }
  };

  const openActionDialog = (ticket, action) => {
    setActionDialog({ open: true, action, ticket, comment: '' });
  };

  const closeActionDialog = () => {
    setActionDialog({ open: false, action: '', ticket: null, comment: '' });
  };

  const submitAction = async () => {
    if (!actionDialog.ticket?.id || !actionDialog.action) return;
    try {
      setProcessing(true);
      const resp = await api.patch(
        `${API_BASE_URL}tickets/${actionDialog.ticket.id}`,
        { action: actionDialog.action, comment: actionDialog.comment || undefined }
      );
      const escalated = resp?.data?.escalated_ticket;
      if (escalated && escalated.ticket_no) {
        showSuccess(t('ticketsEscalatedSuccess', { no: escalated.ticket_no }));
      } else {
        showSuccess(resp?.data?.message || t('ticketsTicketUpdated'));
      }
      closeActionDialog();
      await fetchTickets();
      if (detailsOpen && detailsTicket?.id === actionDialog.ticket.id) {
        await openTicketDetails(actionDialog.ticket.id);
      }
    } catch (e) {
      console.error('Ошибка обновления тикета:', e);
      showError(e?.response?.data?.error || t('ticketsErrorUpdate'));
    } finally {
      setProcessing(false);
    }
  };

  const openCreateDialog = async () => {
    setCreateForm({ client: null, devices: [], title: '', description: '', priority: 'normal', source: 'interface' });
    setClientDevices([]);
    setCreateOpen(true);
    try {
      setClientsLoading(true);
      const resp = await api.get(`${API_BASE_URL}me/clients`);
      setClientsList(Array.isArray(resp?.data?.clients) ? resp.data.clients : []);
    } catch (e) {
      console.error('Ошибка загрузки клиентов:', e);
      setClientsList([]);
      showError(t('ticketsCreateErrorLoadClients'));
    } finally {
      setClientsLoading(false);
    }
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setCreateOpen(false);
  };

  const handleClientChange = async (_, value) => {
    setCreateForm(prev => ({ ...prev, client: value, devices: [] }));
    setClientDevices([]);
    if (!value?.id) return;
    const myReqId = ++clientDevicesReqRef.current;
    try {
      setClientDevicesLoading(true);
      const resp = await api.get(`${API_BASE_URL}me/clients/${value.id}/devices`);
      if (myReqId !== clientDevicesReqRef.current) return;
      setClientDevices(Array.isArray(resp?.data?.devices) ? resp.data.devices : []);
    } catch (e) {
      if (myReqId !== clientDevicesReqRef.current) return;
      console.error('Ошибка загрузки устройств клиента:', e);
      setClientDevices([]);
      showError(t('ticketsCreateErrorLoadDevices'));
    } finally {
      if (myReqId === clientDevicesReqRef.current) {
        setClientDevicesLoading(false);
      }
    }
  };

  const submitCreate = async () => {
    if (!createForm.client?.id || createForm.devices.length === 0) return;
    if (createForm.title.trim().length < 3 || createForm.description.trim().length < 5) return;
    try {
      setCreating(true);
      const payload = {
        client_id: createForm.client.id,
        device_ids: createForm.devices.map(d => d.id),
        ticket_type: 'manual',
        title: createForm.title.trim(),
        description: createForm.description.trim(),
        priority: createForm.priority,
        source: createForm.source
      };
      const resp = await api.post(`${API_BASE_URL}tickets`, payload);
      const no = resp?.data?.ticket?.ticket_no;
      showSuccess(no ? t('ticketsCreateSuccess', { no }) : t('ticketsTicketUpdated'));
      setCreateOpen(false);
      await fetchTickets();
    } catch (e) {
      console.error('Ошибка создания тикета:', e);
      showError(e?.response?.data?.error || t('ticketsCreateErrorSubmit'));
    } finally {
      setCreating(false);
    }
  };

  const canSubmitCreate = !!createForm.client?.id
    && createForm.devices.length > 0
    && createForm.title.trim().length >= 3
    && createForm.description.trim().length >= 5;

  const openEscalateDialog = (ticket) => {
    setEscalateDialog({ open: true, ticket, comment: '', error: '' });
  };

  const closeEscalateDialog = () => {
    if (escalating) return;
    setEscalateDialog({ open: false, ticket: null, comment: '', error: '' });
  };

  const submitEscalate = async () => {
    if (!escalateDialog.ticket?.id) return;
    const reason = (escalateDialog.comment || '').trim();
    if (reason.length === 0) {
      setEscalateDialog(prev => ({ ...prev, error: t('ticketsEscalateReasonRequired') }));
      return;
    }
    try {
      setEscalating(true);
      const resp = await api.patch(
        `${API_BASE_URL}tickets/${escalateDialog.ticket.id}`,
        { action: 'escalate_to_admin', comment: reason }
      );
      const escalated = resp?.data?.escalated_ticket;
      if (escalated?.ticket_no) {
        showSuccess(t('ticketsEscalatedSuccess', { no: escalated.ticket_no }));
      } else {
        showSuccess(resp?.data?.message || t('ticketsTicketUpdated'));
      }
      const sourceTicketId = escalateDialog.ticket.id;
      setEscalateDialog({ open: false, ticket: null, comment: '', error: '' });
      await fetchTickets();
      // Открываем детали нового эскалированного тикета (или обновляем текущие если детали были на исходном)
      if (escalated?.id) {
        await openTicketDetails(escalated.id);
      } else if (detailsOpen && detailsTicket?.id === sourceTicketId) {
        await openTicketDetails(sourceTicketId);
      }
    } catch (e) {
      console.error('Ошибка эскалации тикета:', e);
      const msg = e?.response?.data?.error || t('ticketsErrorUpdate');
      setEscalateDialog(prev => ({ ...prev, error: msg }));
      showError(msg);
    } finally {
      setEscalating(false);
    }
  };

  const getActionTitle = () => {
    switch (actionDialog.action) {
      case 'assign_to_me': return t('ticketsActionTitleAssign');
      case 'send_to_agent': return t('ticketsActionTitleSendToAgent');
      case 'done': return t('ticketsActionTitleDone');
      case 'reject': return t('ticketsActionTitleReject');
      case 'cancel': return t('ticketsActionTitleCancel');
      case 'escalate_to_admin': return t('ticketsActionTitleEscalate');
      default: return t('ticketsActionDefault');
    }
  };

  const getActionMessage = () => {
    if (!actionDialog.ticket) return '';
    const no = actionDialog.ticket.ticket_no;
    switch (actionDialog.action) {
      case 'assign_to_me': return t('ticketsActionMsgAssign', { no });
      case 'send_to_agent': return t('ticketsActionMsgSendToAgent', { no });
      case 'done': return t('ticketsActionMsgDone', { no });
      case 'reject': return t('ticketsActionMsgReject', { no });
      case 'cancel': return t('ticketsActionMsgCancel', { no });
      case 'escalate_to_admin': return t('ticketsActionMsgEscalate', { no });
      default: return '';
    }
  };

  const renderActions = (ticket) => {
    const actions = [];
    const closed = ['done', 'rejected', 'cancelled'].includes(ticket.status);
    const writable = canWriteTicket(ticket);

    if (writable && ticket.status === 'open') {
      actions.push(
        <Button key="assign" size="small" startIcon={<WorkIcon />} onClick={() => openActionDialog(ticket, 'assign_to_me')}>
          {t('ticketsActionAssign')}
        </Button>
      );
    }

    if (writable && ticket.execution_mode === 'agent' && (ticket.status === 'open' || ticket.status === 'in_progress')) {
      actions.push(
        <Button key="agent" size="small" startIcon={<PlayArrowIcon />} onClick={() => openActionDialog(ticket, 'send_to_agent')}>
          {t('ticketsActionSendToAgent')}
        </Button>
      );
    }

    if (writable && !closed) {
      actions.push(
        <Button key="done" size="small" color="success" startIcon={<DoneIcon />} onClick={() => openActionDialog(ticket, 'done')}>
          {t('ticketsActionDone')}
        </Button>
      );
      actions.push(
        <Button key="reject" size="small" color="error" startIcon={<CancelIcon />} onClick={() => openActionDialog(ticket, 'reject')}>
          {t('ticketsActionReject')}
        </Button>
      );
    }

    if (hosterView && ticket.kind === 'client_to_hoster' && !closed) {
      actions.push(
        <Button key="escalate" size="small" color="warning" startIcon={<AssignmentIcon />} onClick={() => openEscalateDialog(ticket)}>
          {t('ticketsActionEscalate')}
        </Button>
      );
    }

    // client_id ≠ user_id для тикета, созданного хостером от имени клиента -- кнопка cancel должна быть и в этом случае
    // Хостер может отозвать собственное обращение к админу (hoster_to_admin, где он автор)
    const canCancel = !closed && (
      clientView
      || (hosterView && ticket.kind === 'hoster_to_admin' && String(ticket.user?.id) === String(user?.id))
    );
    if (canCancel) {
      actions.push(
        <Button key="cancel" size="small" color="error" startIcon={<CancelIcon />} onClick={() => openActionDialog(ticket, 'cancel')}>
          {t('ticketsActionCancel')}
        </Button>
      );
    }

    if (actions.length === 0) {
      return <Typography variant="caption" color="text.secondary">-</Typography>;
    }

    return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{actions}</Box>;
  };

  return (
    <div style={{ backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Menu />
      <div style={{ padding: '20px' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            <AssignmentIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            {t('ticketsTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {clientView ? t('ticketsSubtitleUser') : (hosterView ? t('ticketsSubtitleHoster') : t('ticketsSubtitleAdmin'))}
          </Typography>
        </Box>

        {clientView && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('ticketsNewTicketHint')}
            </Typography>
          </Paper>
        )}

        {(hosterView || adminView) && (
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={kindTab}
              onChange={(_, v) => setKindTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {hosterView && <Tab value="client_to_hoster" label={t('ticketsTabClientToHoster')} />}
              {hosterView && <Tab value="hoster_to_admin" label={t('ticketsTabMyEscalations')} />}
              {adminView && <Tab value="hoster_to_admin" label={t('ticketsTabEscalated')} />}
              {adminView && <Tab value="all" label={t('ticketsTabAll')} />}
            </Tabs>
          </Paper>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>{t('ticketsFilterStatus')}</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label={t('ticketsFilterStatus')}
                >
                  <MenuItem value=""><em>{t('ticketsAllStatuses')}</em></MenuItem>
                  <MenuItem value="open">{t('ticketsStatusOpen')}</MenuItem>
                  <MenuItem value="in_progress">{t('ticketsStatusInProgress')}</MenuItem>
                  <MenuItem value="waiting-agent">{t('ticketsStatusWaitingAgent')}</MenuItem>
                  <MenuItem value="done">{t('ticketsStatusDone')}</MenuItem>
                  <MenuItem value="rejected">{t('ticketsStatusRejected')}</MenuItem>
                  <MenuItem value="cancelled">{t('ticketsStatusCancelled')}</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTickets} disabled={loading}>
                {t('ticketsRefresh')}
              </Button>
              {hosterView && (
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreateDialog}>
                  {t('ticketsCreateOnBehalfButton')}
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={t('ticketsTotalChip', { count: tickets.length })} color="primary" variant="outlined" />
              <Chip label={t('ticketsOpenChip', { count: tickets.filter(tk => tk.status === 'open').length })} color="warning" variant="outlined" />
              <Chip label={t('ticketsInProgressChip', { count: tickets.filter(tk => tk.status === 'in_progress').length })} color="info" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('ticketsColumnNumber')}</TableCell>
                  <TableCell>{t('ticketsColumnTitle')}</TableCell>
                  <TableCell>{t('ticketsColumnType')}</TableCell>
                  <TableCell>{t('ticketsColumnEquipment')}</TableCell>
                  <TableCell>{t('ticketsColumnHosting')}</TableCell>
                  <TableCell>{t('ticketsColumnStatus')}</TableCell>
                  <TableCell>{t('ticketsColumnAssignee')}</TableCell>
                  <TableCell>{t('ticketsColumnUpdated')}</TableCell>
                  <TableCell>{t('ticketsColumnActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography sx={{ py: 4 }}>{t('requestsLoadingData')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : tickets.length > 0 ? (
                  tickets.map((ticket) => (
                    <TableRow key={ticket.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2">#{ticket.ticket_no}</Typography>
                          {ticket.kind && (
                            <Chip size="small" variant="outlined" color={getKindColor(ticket.kind)} label={getKindLabel(ticket.kind, t)} />
                          )}
                          {ticket.source && ticket.source !== 'interface' && (
                            <Chip size="small" variant="outlined" label={getSourceLabel(ticket.source, t)} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => openTicketDetails(ticket.id)}
                          >
                            {ticket.title}
                          </Typography>
                          {ticket.client?.name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('ticketsRowClient', { name: ticket.client.name })}
                            </Typography>
                          )}
                          {ticket.user?.name && ticket.user.id !== ticket.client?.id && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('ticketsRowCreator', { name: ticket.user.name })}
                            </Typography>
                          )}
                          {ticket.escalated_from?.ticket_no && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('ticketsRowEscalatedFrom', { no: ticket.escalated_from.ticket_no })}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Chip size="small" label={getTypeLabel(ticket.ticket_type, t)} />
                          <Chip size="small" variant="outlined" color={getPriorityColor(ticket.priority)} label={getPriorityLabel(ticket.priority, t)} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{t('ticketsDeviceCount', { count: ticket.device_count })}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {(ticket.devices_preview || []).map((d) => (
                            <Chip key={ticket.id + '-' + d.id} size="small" variant="outlined" label={d.hostname || d.ip_address || t('ticketsDeviceFallback')} />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{ticket.hosting?.name || '-'}</TableCell>
                      <TableCell>
                        <Chip size="small" color={getStatusColor(ticket.status)} label={getStatusLabel(ticket.status, t)} />
                        {ticket.agent_summary?.total > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            {t('ticketsAgentSummary', { done: ticket.agent_summary.done, total: ticket.agent_summary.total })}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{ticket.assignee?.name || t('ticketsNotAssigned')}</TableCell>
                      <TableCell>{formatDate(ticket.updated_at)}</TableCell>
                      <TableCell>{renderActions(ticket)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography sx={{ py: 4 }} color="text.secondary">{t('ticketsNotFound')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>

      {/* Детали тикета */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detailsTicket ? t('ticketsDialogTicketTitle', { no: detailsTicket.ticket_no }) : t('ticketsDialogTicket')}
        </DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Typography>{t('requestsLoadingData')}</Typography>
          ) : detailsTicket ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Typography variant="h6">{detailsTicket.title}</Typography>
              <Typography variant="body2" color="text.secondary">{detailsTicket.description}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={getTypeLabel(detailsTicket.ticket_type, t)} />
                <Chip color={getStatusColor(detailsTicket.status)} label={getStatusLabel(detailsTicket.status, t)} />
                <Chip variant="outlined" color={getPriorityColor(detailsTicket.priority)} label={getPriorityLabel(detailsTicket.priority, t)} />
                {detailsTicket.kind && (
                  <Chip variant="outlined" color={getKindColor(detailsTicket.kind)} label={getKindLabel(detailsTicket.kind, t)} />
                )}
                {detailsTicket.source && detailsTicket.source !== 'interface' && (
                  <Chip variant="outlined" label={getSourceLabel(detailsTicket.source, t)} />
                )}
              </Box>
              <Typography variant="body2">{t('ticketsDetailsClient', { name: detailsTicket.client?.name || detailsTicket.user?.name || '-' })}</Typography>
              {detailsTicket.user?.id && detailsTicket.user.id !== detailsTicket.client?.id && (
                <Typography variant="body2">{t('ticketsDetailsCreator', { name: detailsTicket.user.name || '-' })}</Typography>
              )}
              <Typography variant="body2">{t('ticketsDetailsHosting', { name: detailsTicket.hosting?.name || '-' })}</Typography>
              <Typography variant="body2">{t('ticketsDetailsAssignee', { name: detailsTicket.assignee?.name || t('ticketsNotAssigned') })}</Typography>
              <Typography variant="body2">{t('ticketsDetailsCreated', { date: formatDate(detailsTicket.created_at) })}</Typography>
              <Typography variant="body2">{t('ticketsDetailsUpdated', { date: formatDate(detailsTicket.updated_at) })}</Typography>
              {detailsTicket.escalated_from?.ticket_no && (
                <Typography variant="body2" color="warning.main">
                  {t('ticketsDetailsEscalatedFrom', { no: detailsTicket.escalated_from.ticket_no })}
                </Typography>
              )}

              {detailsTicket.result_comment && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>{t('ticketsResultComment')}</Typography>
                  <Typography variant="body2" color="text.secondary">{detailsTicket.result_comment}</Typography>
                </Paper>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom>{t('ticketsEquipment')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(detailsTicket.devices_preview || []).map((d) => (
                    <Chip key={detailsTicket.id + '-' + d.id} variant="outlined" label={d.hostname || d.ip_address || d.id} />
                  ))}
                </Box>
              </Box>

              {detailsTicket.agent_summary?.total > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>{t('ticketsAgentStatuses')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('ticketsAgentSummaryDetails', { total: detailsTicket.agent_summary.total, done: detailsTicket.agent_summary.done, waitingAgent: detailsTicket.agent_summary.waiting_agent, agentError: detailsTicket.agent_summary.agent_error })}
                  </Typography>
                </Paper>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">{t('ticketsDetailsNotFound')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>{t('usersCancelButton')}</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог эскалации - обязательная причина, отдельный flow */}
      <Dialog open={escalateDialog.open} onClose={closeEscalateDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('ticketsActionTitleEscalate')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {escalateDialog.ticket ? t('ticketsActionMsgEscalate', { no: escalateDialog.ticket.ticket_no }) : ''}
          </Typography>
          <TextField
            label={t('ticketsEscalateReasonLabel')}
            value={escalateDialog.comment}
            onChange={(e) => setEscalateDialog(prev => ({ ...prev, comment: e.target.value, error: '' }))}
            fullWidth
            required
            multiline
            minRows={3}
            error={!!escalateDialog.error}
            helperText={escalateDialog.error || ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEscalateDialog} disabled={escalating}>{t('usersCancelButton')}</Button>
          <Button onClick={submitEscalate} variant="contained" color="warning" disabled={escalating || escalateDialog.comment.trim().length === 0}>
            {escalating ? t('ticketsSaving') : t('ticketsActionEscalate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог действия */}
      <Dialog open={actionDialog.open} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{getActionTitle()}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getActionMessage()}
          </Typography>
          <TextField
            label={t('ticketsCommentLabel')}
            value={actionDialog.comment}
            onChange={(e) => setActionDialog(prev => ({ ...prev, comment: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>{t('usersCancelButton')}</Button>
          <Button onClick={submitAction} variant="contained" disabled={processing}>
            {processing ? t('ticketsSaving') : t('usersConfirmButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('ticketsCreateOnBehalfTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Autocomplete
              options={clientsList}
              value={createForm.client}
              onChange={handleClientChange}
              getOptionLabel={(opt) => opt?.name ? `${opt.name} (${opt.email || '-'})` : (opt?.email || '')}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              loading={clientsLoading}
              noOptionsText={clientsLoading ? t('ticketsSaving') : t('ticketsCreateNoClients')}
              renderInput={(params) => (
                <TextField {...params} label={t('ticketsCreateClientLabel')} placeholder={t('ticketsCreateClientPlaceholder')} />
              )}
            />

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={clientDevices}
              value={createForm.devices}
              onChange={(_, value) => setCreateForm(prev => ({ ...prev, devices: value }))}
              getOptionLabel={(opt) => opt?.hostname || opt?.ip_address || opt?.id || ''}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              loading={clientDevicesLoading}
              disabled={!createForm.client?.id}
              noOptionsText={
                !createForm.client?.id
                  ? t('ticketsCreateClientPlaceholder')
                  : (clientDevicesLoading ? t('ticketsSaving') : t('ticketsCreateDevicesEmpty'))
              }
              renderTags={(value, getTagProps) =>
                value.map((opt, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={opt.id}
                    size="small"
                    label={opt.hostname || opt.ip_address || opt.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label={t('ticketsCreateDevicesLabel')} placeholder={t('ticketsCreateDevicesPlaceholder')} />
              )}
            />

            <TextField
              label={t('ticketsCreateTitleLabel')}
              value={createForm.title}
              onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
              fullWidth
              required
            />

            <TextField
              label={t('ticketsCreateDescriptionLabel')}
              value={createForm.description}
              onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              required
              multiline
              minRows={3}
            />

            <FormControl fullWidth>
              <InputLabel>{t('ticketsCreatePriorityLabel')}</InputLabel>
              <Select
                value={createForm.priority}
                onChange={(e) => setCreateForm(prev => ({ ...prev, priority: e.target.value }))}
                label={t('ticketsCreatePriorityLabel')}
              >
                <MenuItem value="low">{t('ticketsPriorityLow')}</MenuItem>
                <MenuItem value="normal">{t('ticketsPriorityNormal')}</MenuItem>
                <MenuItem value="high">{t('ticketsPriorityHigh')}</MenuItem>
                <MenuItem value="urgent">{t('ticketsPriorityUrgent')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('ticketsCreateSourceLabel')}</InputLabel>
              <Select
                value={createForm.source}
                onChange={(e) => setCreateForm(prev => ({ ...prev, source: e.target.value }))}
                label={t('ticketsCreateSourceLabel')}
              >
                <MenuItem value="interface">{t('ticketsSourceInterface')}</MenuItem>
                <MenuItem value="email">{t('ticketsSourceEmail')}</MenuItem>
                <MenuItem value="telegram">{t('ticketsSourceTelegram')}</MenuItem>
              </Select>
              <FormHelperText>{t('ticketsCreateSourceHint')}</FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={creating}>{t('usersCancelButton')}</Button>
          <Button onClick={submitCreate} variant="contained" disabled={!canSubmitCreate || creating}>
            {creating ? t('ticketsCreateSubmitting') : t('ticketsCreateSubmit')}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationContainer />
    </div>
  );
}
